import React, { useState } from 'react';
import { Attachment } from 'src/api/generated';
import { AudioMedia } from './audio-media';
import { FileMedia } from './file-media';
import { ImageMedia } from './image-media';
import { VideoMedia } from './video-media';
import { MediaProcessingDisplay } from './media-processing-display';
import { MediaProcessingButtons } from 'src/modules/communications/components/molecules/media/media-processing-buttons';
import { getBestFileName } from '../../../utils/attachment.util';

interface AttachmentListProps {
  attachments: Attachment[];
  className?: string;
  isLoading?: boolean;
}

export const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments = [],
  className = '',
  isLoading = false,
}) => {
  const [displayContent, setDisplayContent] = useState<string | null>(null);
  const [displayType, setDisplayType] = useState<
    'textRepresentation' | 'summary' | null
  >(null);

  if (attachments.length === 0) return null;

  const handleShowText = (
    content: string,
    type: 'textRepresentation' | 'summary'
  ) => {
    setDisplayContent(content);
    setDisplayType(type);
  };

  const handleCloseDisplay = () => {
    setDisplayContent(null);
    setDisplayType(null);
  };

  const isSingleAttachment = attachments.length === 1;
  const gridClasses = `flex justify-start ${
    isSingleAttachment ? '' : 'gap-2 overflow-x-auto'
  } ${className}`;

  return (
    <div className="flex flex-col gap-2 justify-end">
      <div className={gridClasses}>
        {attachments.map((attachment) => {
          const { id, type, url, mediaProcessing } = attachment;

          switch (type) {
            case 'image':
              return (
                <ImageMedia
                  key={id}
                  url={url}
                  isLoading={isLoading}
                  footer={
                    <MediaProcessingButtons
                      mediaProcessing={mediaProcessing}
                      onShowText={handleShowText}
                      className="mt-1"
                    />
                  }
                />
              );
            case 'video':
              return (
                <VideoMedia
                  key={id}
                  url={url}
                  isLoading={isLoading}
                  footer={
                    <MediaProcessingButtons
                      mediaProcessing={mediaProcessing}
                      onShowText={handleShowText}
                      className="mt-1"
                    />
                  }
                />
              );
            case 'audio':
              return (
                <AudioMedia
                  key={id}
                  url={url}
                  isLoading={isLoading}
                  footer={
                    <MediaProcessingButtons
                      mediaProcessing={mediaProcessing}
                      onShowText={handleShowText}
                      className="mt-1"
                    />
                  }
                />
              );
            case 'file':
              return (
                <FileMedia
                  key={id}
                  url={url}
                  fileName={getBestFileName(attachment)}
                  isLoading={isLoading}
                  footer={
                    <MediaProcessingButtons
                      mediaProcessing={mediaProcessing}
                      onShowText={handleShowText}
                      className="mt-1"
                    />
                  }
                />
              );
            default:
              return null;
          }
        })}
      </div>

      <MediaProcessingDisplay
        content={displayContent}
        type={displayType}
        onClose={handleCloseDisplay}
      />
    </div>
  );
};
